import * as React from 'react'
import { useState } from 'react'
import { Flex, Spinner } from '@radix-ui/themes'
import { Link } from '@tanstack/react-router'
import { useGetTutorStudents } from '@/services/tutorService'
import { useClassStore } from '@/stores/useClassStore'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'
import SearchBox from '@/components/ui/SearchBox'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import TablePaginate from '@/components/ui/table-paginate'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TutorTableTabs } from '../constants'

interface TutorStudentTableProps {
  classId: string
  className?: string
  title?: string
  tableTab: string
  setTableTab: (tab: string) => void
}

export const TutorStudentTable: React.FC<TutorStudentTableProps> = ({
  classId,
  className,
  tableTab,
  setTableTab,
}) => {
  const { setSelectedUserRole } = useClassStore()

  const [page, setPage] = useState(1)
  const [query, setQuery] = useState('')

  const debouncedQuery = useDebounce(query)

  const { tutorStudents, isLoadingTutorStudents, meta } = useGetTutorStudents({
    page,
    search: debouncedQuery.trim(),
    classId,
  })

  return (
    <>
      <Card className='w-full gap-4 border-none py-5'>
        <div className='border-b border-[#EAECF0]'>
          <Tabs
            value={tableTab}
            onValueChange={setTableTab}
            defaultValue={TutorTableTabs.student}
            className='w-full flex-col gap-3 overflow-auto px-4'
          >
            <TabsList className='gap-1 bg-transparent'>
              {Object.values(TutorTableTabs).map((item: string) => (
                <TabsTrigger
                  value={item}
                  key={item}
                  className='max-h-11 text-base data-[state=inactive]:text-[#667085]'
                >
                  {item}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <CardContent className='p-0'>
          <div
            className={cn('space-y-6 rounded-lg bg-[#FFFFFF] p-4', className)}
          >
            <div className='flex max-h-10 items-center justify-between'>
              <SearchBox
                containerClass='max-h-10'
                onValueChange={(value: string) => setQuery(value)}
              />
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className='bg-[#F2F4F7]'>
                <TableHead className='font-mundial px-6 text-sm font-[600] text-[#002652]'>
                  Name
                </TableHead>
                <TableHead className='font-mundial text-sm font-[600] text-[#002652]'></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className='text-[#002652]'>
              {isLoadingTutorStudents ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Flex justify={'center'}>
                      <Spinner />
                    </Flex>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {tutorStudents?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className='h-24 text-center'>
                        No data found.
                      </TableCell>
                    </TableRow>
                  )}
                  {tutorStudents?.map((item) => (
                    <TableRow
                      key={item.student_id}
                      className='hover:bg-gray-50'
                    >
                      <TableCell className='w-[167px] px-6'>
                        <div className='w-[100px] font-[400] text-[#002652]'>
                          {item.fullname}
                        </div>
                        <p className='text-[#475467]'>{item.email}</p>
                      </TableCell>
                      <TableCell className='w-[119px] pr-4 text-end'>
                        <Link
                          to='/users/$studentId'
                          params={{ studentId: item.student_id.toString() }}
                          className='rounded px-3 py-2 text-[#0057B8] hover:bg-[#0057B8]/10'
                          onClick={() => setSelectedUserRole('student')}
                        >
                          View detail
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </TableBody>
          </Table>

          {meta && meta.total_pages > 1 && (
            <div className='flex justify-end px-8 py-4'>
              <TablePaginate
                itemPerPage={meta.current_items}
                totalItems={meta.total_items}
                page={page}
                totalPages={meta.total_pages}
                onPageChange={setPage}
                readOnlyPageDisplay={false}
                hideOnSinglePage={false}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}

export default TutorStudentTable
